import { Component, useEffect, useRef, useState } from 'react';
import { useCallback } from 'react';
import { Edge, Handle, NodeProps, Position, ReactFlowInstance, useReactFlow, useStore, XYPosition } from 'reactflow';
import { getChatGPTKeywords, getChatGPTLines, getChatGPTRelatedTopics, getChatGPTResponse, getChatGPTResponseStream, getChatGPTSummary } from '../../../api/openai-api';
import './response-node.scss';
import { ReactComponent as DragHandle } from '../../../assets/drag-handle.svg';
import { isHighlightable } from '../../triggers/highlighter/highlighter';
import HighlightTooltip from './highlight-tooplip/highlight-toolip';
import { Tooltip, TooltipProvider, TooltipWrapper } from 'react-tooltip';
import { createRoot } from 'react-dom/client';
import { ChatNodeData, TypeChatNode } from './response-node.model';
import GPTInput from '../../components/gpt-input/gpt-input';
import { ResponseState } from '../../components/input.model';
import { createChatNode, createFollowUpChatNode } from './response-node.helper';
import { InputHoverState, ZoomState } from '../node.model';
import { InstanceState } from '../../triggers/semantic-dive/semantic-dive';
import { TopicNodeData } from '../topic-node/topic-node.model';

import { DownloadOutlined } from '@ant-design/icons';
import { Button, Radio, Space, Divider } from 'antd';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import loadingDots from '../../../assets/loading.gif';

import { ChatCompletionRequestMessage } from "openai";
import { devFlags } from '../../utils';
import { createTopicNode } from '../topic-node/topic-node.helper';
import { extractNodeTexts, predictSubtopics } from '../node.helper';

const https = require('https-browserify'); // using it to stream text from openAI

type ChatState = {
  input: string,
  response: string, // Singular response. more follow ups/responses belong in another chatnode
  responseIsLoading: boolean,
  highlightIds: string[],
  currentHighlightId: string,
  reactFlowInstance: ReactFlowInstance,
};

const zoomSelector = (s: any) => s.transform[2];

const ChatNode = (props: NodeProps) => {
  const [input, setInput] = useState(props.data.state.input ?? '');

  // TODO: Combine 3 responses into one object
  const [response, setResponse] = useState(props.data.state.response ?? '');
  const [lines, setLines] = useState(props.data.state.lines ?? '');
  const [summary, setSummary] = useState(props.data.state.summary ?? '');
  const [keywords, setKeywords] = useState(props.data.state.keywords ?? '');
  const [semanticMode, setSemanticMode] = useState<ZoomState>(ZoomState.NONE); // default semantic mode; ZoomState.NONE means the mode is in AUTO and Semantic Zoom will be based on zoom level
  const [loadingLines, setLoadingLines] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingKeywords, setLoadingKeywords] = useState(false);

  const [responseInputState, setResponseInputState] = useState<ResponseState>(props.data.state.responseInputState ?? ResponseState.INPUT);
  const [highlightIds, setHighlightIds] = useState<string[]>(props.data.state.highlightIds ?? []);

  const zoom: number = useStore(zoomSelector);

  const reactFlowInstance = useReactFlow();

  const observer = new ResizeObserver(chatElements => {
    for (const chatElement of chatElements) {
      // console.log('cha');
    }
  });

  useEffect(() => {
    reactFlowInstance.setNodes((nodes) => nodes.map(node => {
      if (node.id === props.id) {
        node.data.state = {
          input,
          response,
          lines,
          summary,
          keywords,
          responseInputState,
          highlightIds
        };
      }
      return node;
    }));
  }, [reactFlowInstance, input, response, lines, summary, keywords, responseInputState, highlightIds]);

  useEffect(() => {
    // If a response is already given, don't take in any input.
    if (props.data.state.input && props.data.state.responseInputState === ResponseState.LOADING) {
      generateResponse(input);
    } else if (props.data.state.responseInputState === ResponseState.INPUT) {
      const currElement = document.querySelectorAll(`[data-id="${props.id}"]`)[0];
      const inputElement = currElement.getElementsByClassName('text-input')[0] as HTMLInputElement;
      observer.observe(currElement);
      // add focus to new node's input element (by doing this, viewport adjusts to it)
      // setTimeout(() => {
      //   inputElement.focus();
      // }, 100);
    }
  }, []);

  const addChatFollowUpNode = useCallback(
    (input: string, response: string) => {
      const data: ChatNodeData = {
        parentId: props.id,
        chatHistory: [
          ...props.data.chatHistory,
          {
            role: 'user',
            content: input
          },
          {
            role: 'assistant',
            content: response,
          }
        ],
        // We want chat node to have no response yet, since the user will ask for a response
        followUpNode: true, // flag used to hide follow up node when user zooms out
        placeholder: 'Ask a follow up question',
        state: {},
      };
      // createChatNode(reactFlowInstance, props.id, data);
      createFollowUpChatNode(reactFlowInstance, props.id, data, observer);
    },
    [reactFlowInstance]
  )

  const generateResponse = useCallback(async (prompt: string) => {

    if (!prompt) return;
    setResponseInputState(ResponseState.LOADING);

    // Stream text
    return getChatGPTResponseStream(props.data.chatHistory, prompt, (text: string) => {
      // save to response and update text in node
      setResponse(text);
    }).then(result => {
      setResponseInputState(ResponseState.COMPLETE);
      if (!result) {
        return false;
      }
      (props.data as ChatNodeData).chatHistory.push({
        role: 'assistant',
        content: result,
      })
      generateSummaries(result); // generate summaries of generated response
      // predictSubtopics([result], [props.id], reactFlowInstance, 'right-side');
      addChatFollowUpNode(prompt, result); // add follow up node once stream is done
      return true;
    }).catch(() => {
      return false;
    });
  }, [response]);

  /**
   * Semantic Zoom
   * Generates the summary and keywords of response
   */
  const generateSummaries = (text: string) => {
    if (!text) return;

    setLoadingLines(true);
    setLoadingSummary(true);
    setLoadingKeywords(true);

    // Only apply lines when there are new lines
    getChatGPTLines(text).then(data => {
      setLines(data);
      setLoadingLines(false);
    });

    getChatGPTSummary(text).then(data => {
      setSummary(data);
      setLoadingSummary(false);
    });

    getChatGPTKeywords(text).then(data => {
      setKeywords(data);
      setLoadingKeywords(false)
    });
  }

  // When a highlight is dragged out of text
  const onDragStart = (event: any, nodeType: string, topicName: string) => {
    event.dataTransfer.setData('dragNodeType', nodeType);
    event.dataTransfer.effectAllowed = 'move';
    const currNode: TypeChatNode | undefined = reactFlowInstance.getNode(props.id);
    if (!currNode) {
      return;
    }
    const data = JSON.stringify({
      parentId: props.id,
      chatHistory: [
        ...currNode.data.chatHistory,
        {
          role: 'user',
          content: input
        },
        {
          role: 'assistant',
          content: response,
        }
      ],
      instanceState: InstanceState.NONE,
      state: {
        topic: topicName
      }
    } as TopicNodeData);
    event.dataTransfer.setData('dragNodeData', data);
  };

  const highlightSelection = () => {
    const selection = document.getSelection();
    if ((selection?.rangeCount ?? 0) <= 0) {
      return;
    }
    const range: Range = selection?.getRangeAt(0) ?? new Range;
    const selectedText = range.toString();
    if (isHighlightable(range)) {
      const highlight = document.createElement('highlight-text', {is: selectedText});
      highlight.draggable = true;
      highlight.innerHTML = selectedText;
      range.surroundContents(highlight);
      highlight.classList.add('highlight-elm');
      highlight.id = `highlight-${highlightIds.length}`;
      setHighlightIds(highlightIds.concat([highlight.id]));

      highlight.addEventListener('click', (event) => {

        if ((event.altKey || event.metaKey || event.shiftKey) && event.detail === 1) { // delete highlight when clicked with alt, command, or shift
          range.extractContents();
          range.insertNode(document.createTextNode(selectedText));
          range.commonAncestorContainer.normalize();
        } else if (event.detail === 1) {
          // generate topic node on the right side

          const currNode: TypeChatNode | undefined = reactFlowInstance.getNode(props.id);
          if (!currNode) {
            return;
          }

          // get chat node position
          const xy_position = currNode?.position!;
          const y_position_absolute = currNode?.positionAbsolute!['y'];
          const height = currNode?.height!;
          const width = currNode?.width!;

          const highlightDiv = document.getElementById(highlight.id); 

          const data = JSON.stringify({
            parentId: props.id,
            chatHistory: [
              ...currNode.data.chatHistory,
              {
                role: 'user',
                content: input
              },
              {
                role: 'assistant',
                content: response,
              }
            ],
            instanceState: InstanceState.NONE,
            state: {
              topic: selectedText
            }
          } as TopicNodeData);

          let position: XYPosition;
          position = {
            x: xy_position['x'] + (width / 2) + (highlightDiv?.offsetLeft! * 0.7) + 80, // place chat node on the right of brainstorm node
            y: y_position_absolute + highlightDiv?.offsetTop! + highlightDiv?.parentElement?.offsetTop! + highlightDiv?.parentElement?.parentElement?.offsetTop! - 30, // place chat node next to question div relative to brainstorm node (offsetTop)
          };
          createTopicNode(selectedText, props.id, position, [], false, reactFlowInstance);
        } else if (event.detail === 2) { // delete highlight when you double click
          range.extractContents();
          range.insertNode(document.createTextNode(selectedText));
          range.commonAncestorContainer.normalize();
        }
      })
      highlight.addEventListener('dragstart', (event) => {
        onDragStart(event, 'topic', selectedText);
      })
      window.getSelection()?.removeAllRanges();
    }
  }

  // Depending on Zoom level, show response by length
  const isZoomState = useCallback((responseReturnType: ZoomState) => {
    // Get the follow up node
    // const followUpNode = reactFlowInstance.getNodes().find(node => node.parentNode === props.id);
    // if (followUpNode) {
    //   // reposition the follow up node
    //   reactFlowInstance.setNodes(nodes => nodes.map(node => {
    //     if (node.id === followUpNode.id) {
    //       const parent = reactFlowInstance.getNode(node.parentNode ?? '');
    //       node.position.y = (parent?.height ?? 0) + 20;
    //     }
    //     return node;
    //   }))
    // }
    // if summary and keywords both haven't loaded, show default
    if (semanticMode === ZoomState.NONE) { // if user hasn't clicked button yet, apply semantic zoom based on zoom level
      if (!lines || !(summary && keywords) || zoom > ZoomState.ALL) {
        return responseReturnType === ZoomState.ALL ? 'chat-text-show' : '';
      } else if (zoom > ZoomState.LINES) {
        return responseReturnType === ZoomState.LINES ? 'chat-text-show' : '';
      } else if (zoom > ZoomState.SUMMARY) {
        return responseReturnType === ZoomState.SUMMARY ? 'chat-text-show' : '';
      } else {
        return responseReturnType === ZoomState.KEYWORDS ? 'chat-text-show' : '';
      }
    } else { // if user clicks any of the semantic level button, prioritize the selected button (and ignore the semantic zoom based on zoom level)
      if (semanticMode === ZoomState.ALL || (semanticMode === ZoomState.LINES && !lines)) {
        return responseReturnType === ZoomState.ALL ? 'chat-text-show' : '';
      } else if (semanticMode === ZoomState.LINES) {
        return responseReturnType === ZoomState.LINES ? 'chat-text-show' : '';
      } else if (semanticMode === ZoomState.SUMMARY) {
        return responseReturnType === ZoomState.SUMMARY ? 'chat-text-show' : '';
      } else if (semanticMode === ZoomState.KEYWORDS) {
        return responseReturnType === ZoomState.KEYWORDS ? 'chat-text-show' : '';
      }
    }
    return '';
  }, [semanticMode, zoom, lines, summary, keywords]);

  // Idea: summaryzoom >= ALL

  return (
    // Allow highlighting only for fully expanded text
    // <div className='chat-node highlightable'>
    <div 
      className={`node chat-node ${zoom >= ZoomState.ALL ? 'highlightable' : 'drag-handle'}`}
      style={{ display: (props.data.followUpNode && zoom <= ZoomState.ALL) ? 'none' : 'block' }}
    >
      <DragHandle className='drag-handle' />
      <GPTInput
        sourceId={props.id}
        responseState={responseInputState}
        setResponseState={setResponseInputState}
        generateResponse={generateResponse}
        placeholder={props.data.placeholder}
        input={input}
        setInput={setInput}
        setInputState={(s: InputHoverState) => {}}
      />
      { props.data.state.responseInputState === ResponseState.COMPLETE ? (
        <div className="semantic-zoom-control">
          <Radio.Group
            value={semanticMode}
            onChange={(e) => setSemanticMode(e.target.value)}
            size='small'
          >
            <Radio.Button value={ZoomState.ALL}>All</Radio.Button>
            <Radio.Button value={ZoomState.LINES}>Lines</Radio.Button>
            <Radio.Button value={ZoomState.SUMMARY}>Summary</Radio.Button>
            <Radio.Button value={ZoomState.KEYWORDS}>Keywords</Radio.Button>
            <Radio.Button value={0}>Auto</Radio.Button>
          </Radio.Group>
        </div>
      ): <></>}
      <div
        id='highlight-box'
        className='highlight-box'
        onMouseUp={highlightSelection}
      >
        {/* <div className='chat-response'>
          this is a temporary response block because GPT-3 is not working for me right now. Idk why but I hope it works soon so I can ask questions. I have a lot of questions that need to be answered.
        </div> */}
        {/* {response ? (<ResponseByZoom />) : <></>} */}
        {response ? (
          <>
            <div className={`chat-response all ${isZoomState(ZoomState.ALL)}`}>{response}</div>
            <div className={`chat-response shortened lines ${isZoomState(ZoomState.LINES)} ${loadingLines ? 'center' : ''}`}>{loadingLines ? <img width="18px" height="18px" src={loadingDots} alt="loading..." /> : lines}</div>
            <div className={`chat-response shortened summary ${isZoomState(ZoomState.SUMMARY)} ${loadingSummary ? 'center' : ''}`}>{loadingSummary ? <img width="18px" height="18px" src={loadingDots} alt="loading..." /> : summary}</div>
            <div className={`chat-response shortened keywords ${isZoomState(ZoomState.KEYWORDS)} ${loadingKeywords ? 'center' : ''}`}>{loadingKeywords ? <img width="18px" height="18px" src={loadingDots} alt="loading..." /> : keywords}</div>
          </>
        ) : <></>}
      </div>
      <Handle id="target-e-1" type="target" position={Position.Top} className="node-handle-direct "/>
      <Handle id="source-e-1" type="source" position={Position.Bottom} className="node-handle-direct" />
    </div>
  )

}

export default ChatNode;
